/*
  Purchase options, rendered by snippets/product-purchase-options.liquid: the size (a single unit, or
  the bulk option) as two cards, then the ways to buy (one-time, each subscription group) as radio
  cards, each priced for the chosen size and quantity, with the delivery frequency inside the chosen
  subscription.

  The shopper's choices live in this.state and are written to the product form: the plan into the
  hidden `selling_plan` input, the quantity into the form's quantity input. That is the theme's own
  selector beside Add to cart, which sets the single-unit quantity (the cards' prices follow it). While
  the bulk option is chosen the same selector counts boxes: it's taken out of the form (unnamed) and a
  hidden quantity input sends boxes x the bulk quantity (2 boxes = 12 bags). Each size keeps its own
  count. The cards are swapped for the new variant's prices when the variant changes.
*/
if (!customElements.get('purchase-options')) {
  class PurchaseOptions extends HTMLElement {
    connectedCallback() {
      this.state = { option: null, plans: {}, size: 'unit', qty: 1, boxes: 1 };
      this.state.qty = this.clampQty(parseInt(this.quantityInput?.value, 10) || 1);
      this.readState();

      this.addEventListener('change', this.onChange.bind(this));
      this.addEventListener('click', this.onClick.bind(this));
      this.onQuantityChange = this.onQuantityChange.bind(this);
      document.addEventListener('change', this.onQuantityChange);
      // Read the selector again just before the form is sent (capture, so ahead of the theme's own submit
      // handler), and after the theme resets it (it does so without a change event).
      this.onSubmit = this.onSubmit.bind(this);
      document.addEventListener('submit', this.onSubmit, true);
      this.unsubscribe = window.theme?.pubsub?.subscribe(
        theme.pubsub.PUB_SUB_EVENTS.variantChange,
        this.onVariantChange.bind(this)
      );
      this.unsubscribeBoundaries = window.theme?.pubsub?.subscribe(
        theme.pubsub.PUB_SUB_EVENTS.quantityBoundries,
        () => setTimeout(() => this.readQuantity())
      );

      this.applyState();
    }

    disconnectedCallback() {
      this.unsubscribe?.();
      this.unsubscribeBoundaries?.();
      document.removeEventListener('change', this.onQuantityChange);
      document.removeEventListener('submit', this.onSubmit, true);
    }

    get list() {
      return this.querySelector('.purchase-options__list');
    }

    get planInput() {
      return this.querySelector('input[name="selling_plan"]');
    }

    get formId() {
      return this.planInput?.getAttribute('form');
    }

    get bulkQuantity() {
      return parseInt(this.dataset.bulkQuantity, 10) || null;
    }

    // The bulk option is chosen and has a quantity: the selector counts boxes.
    get countsBoxes() {
      return this.state.size === 'bulk' && !!this.bulkQuantity;
    }

    // The form's own quantity input (the theme's selector, unnamed while it counts boxes); if the template
    // has none, add one.
    get quantityInput() {
      if (this._quantityInput?.isConnected) return this._quantityInput;
      const formId = this.formId;
      if (!formId) return null;
      const form = document.getElementById(formId);
      const selector = 'input:is([name="quantity"], [data-box-count]):not([data-bulk-field])';
      let input = document.querySelector(`${selector}[form="${formId}"]`) || form?.querySelector(selector);
      if (!input) {
        input = document.createElement('input');
        input.type = 'hidden';
        input.name = 'quantity';
        input.value = '1';
        input.setAttribute('form', formId);
        this.append(input);
      }
      this._quantityInput = input;
      return input;
    }

    // What the form sends as the quantity while the selector counts boxes.
    get bulkField() {
      if (!this._bulkField) {
        const input = document.createElement('input');
        input.type = 'hidden';
        input.name = 'quantity';
        input.disabled = true;
        input.dataset.bulkField = '';
        input.setAttribute('form', this.formId);
        this.append(input);
        this._bulkField = input;
      }
      return this._bulkField;
    }

    rowKey(row) {
      return row?.dataset.option === 'subscription' ? row.dataset.groupId : row?.dataset.option;
    }

    clampQty(value) {
      const input = this.quantityInput;
      const min = parseInt(input?.min, 10) || 1;
      const max = parseInt(input?.max, 10) || Infinity;
      return Math.min(Math.max(value, min), max);
    }

    // Pick up what the server rendered as checked.
    readState() {
      const checked = this.querySelector('.purchase-options__radio:checked');
      this.state.option = checked?.value || this.querySelector('.purchase-options__radio')?.value || null;
      this.querySelectorAll('.purchase-options__option--subscription').forEach((row) => {
        const plan = row.querySelector('.purchase-options__plan-radio:checked');
        if (plan) this.state.plans[row.dataset.groupId] = plan.value;
      });
      this.state.size = this.querySelector('.purchase-options__size-radio:checked')?.value || 'unit';
    }

    onChange(event) {
      const target = event.target;
      const row = target.closest('.purchase-options__option');

      if (target.classList.contains('purchase-options__radio')) {
        this.state.option = target.value;
      } else if (target.classList.contains('purchase-options__plan-radio')) {
        this.state.plans[row.dataset.groupId] = target.value;
        this.state.option = this.rowKey(row);
      } else if (target.classList.contains('purchase-options__size-radio')) {
        this.state.size = target.value;
      } else {
        return;
      }

      this.applyState();
    }

    // The "?" beside a subscription's name only shows its tip; it doesn't pick the subscription.
    onClick(event) {
      if (event.target.closest('.purchase-options__help')) event.preventDefault();
    }

    // The theme's quantity selector sets the single-unit quantity, or the number of boxes; the cards'
    // prices follow it.
    onQuantityChange(event) {
      if (this.writingQuantity || event.target !== this.quantityInput || this.contains(event.target)) return;
      this.readQuantity();
    }

    // getAttribute: the form's `id` property is its variant input (name="id").
    onSubmit(event) {
      if (event.target.getAttribute?.('id') === this.formId) this.readQuantity();
    }

    readQuantity() {
      const input = this.quantityInput;
      if (!input || this.hidden) return;
      const value = this.clampQty(parseInt(input.value, 10) || 1);
      if (this.countsBoxes) this.state.boxes = value;
      else this.state.qty = value;
      this.applyState();
    }

    // Mirror the state into the cards, then write it to the form.
    applyState() {
      const state = this.state;

      const optionRadio = this.querySelector(`.purchase-options__radio[value="${state.option}"]`)
        || this.querySelector('.purchase-options__radio');
      if (optionRadio) {
        optionRadio.checked = true;
        state.option = optionRadio.value;
      }
      const selected = optionRadio?.closest('.purchase-options__option');

      this.querySelectorAll('.purchase-options__option').forEach((row) => {
        row.classList.toggle('is-selected', row === selected);
      });

      // Each subscription card: its plan, and that plan's prices for the card.
      this.querySelectorAll('.purchase-options__option--subscription').forEach((row) => {
        const groupId = row.dataset.groupId;
        const plan = row.querySelector(`.purchase-options__plan-radio[value="${state.plans[groupId]}"]`)
          || row.querySelector('.purchase-options__plan-radio:checked')
          || row.querySelector('.purchase-options__plan-radio');
        if (!plan) return;
        plan.checked = true;
        state.plans[groupId] = plan.value;

        const price = row.querySelector('[data-price]');
        if (price) {
          price.dataset.unitCents = plan.dataset.unitCents || '';
          price.dataset.unitCompareCents = plan.dataset.unitCompareCents || '';
          price.dataset.bulkHtml = plan.dataset.bulkPriceHtml || '';
          price.dataset.bulkCents = plan.dataset.bulkCents || '';
          price.dataset.bulkCompareCents = plan.dataset.bulkCompareCents || '';
        }
      });

      // The size. Without a bulk option (or after a variant change that drops it) it's the single unit.
      if (state.size === 'bulk' && !this.querySelector('.purchase-options__size-radio[value="bulk"]')) state.size = 'unit';
      const sizeRadio = this.querySelector(`.purchase-options__size-radio[value="${state.size}"]`);
      if (sizeRadio) sizeRadio.checked = true;
      this.dataset.size = state.size;
      this.dataset.way = selected?.dataset.option || '';

      this.renderPrices();

      // Into the form. While hidden (no choices for this variant) the theme's own selector rules.
      if (this.hidden) {
        if (this.planInput) this.planInput.disabled = true;
        this.useQuantitySelector(false);
        return;
      }

      const planId = selected?.dataset.option === 'subscription' ? state.plans[selected.dataset.groupId] || '' : '';
      if (this.planInput) {
        this.planInput.value = planId;
        this.planInput.disabled = planId === '';
      }

      const boxes = this.countsBoxes;
      const shown = boxes ? state.boxes : state.qty;
      const quantity = boxes ? state.boxes * this.bulkQuantity : state.qty;
      this.useQuantitySelector(boxes);
      const quantityInput = this.quantityInput;
      if (quantityInput && quantityInput.value !== String(shown)) {
        this.writingQuantity = true;
        quantityInput.value = shown;
        quantityInput.dispatchEvent(new Event('change', { bubbles: true }));
        this.writingQuantity = false;
      }
      if (boxes) this.bulkField.value = quantity;

      this.dispatchEvent(new CustomEvent('purchase-options:change', {
        bubbles: true,
        detail: { sellingPlanId: planId || null, quantity }
      }));
    }

    // The selector is the form's quantity (single unit), or counts boxes beside a hidden quantity input.
    useQuantitySelector(countsBoxes) {
      const input = this.quantityInput;
      if (input && !this.contains(input)) {
        if (countsBoxes) {
          input.dataset.boxCount = '';
          input.removeAttribute('name');
        } else if (input.hasAttribute('data-box-count')) {
          input.name = 'quantity';
          input.removeAttribute('data-box-count');
        }
      }
      if (countsBoxes || this._bulkField) this.bulkField.disabled = !countsBoxes;
    }

    // Each card's price for the chosen size and count: a box's price times the boxes, or the single
    // unit's times the quantity.
    renderPrices() {
      this.querySelectorAll('[data-price]').forEach((el) => {
        if (this.state.size === 'bulk' && el.dataset.bulkHtml) {
          if (!this.renderPrice(el, el.dataset.bulkCents, el.dataset.bulkCompareCents, this.state.boxes)) {
            el.innerHTML = el.dataset.bulkHtml;
          }
        } else {
          this.renderPrice(el, el.dataset.unitCents, el.dataset.unitCompareCents, this.state.qty);
        }
      });
    }

    renderPrice(el, centsValue, compareValue, qty) {
      const cents = parseInt(centsValue, 10);
      const format = window.theme?.Currency?.formatMoney;
      if (Number.isNaN(cents) || !format) return false;

      const settings = window.theme.settings || {};
      const moneyFormat = settings.currencyCodeEnabled ? settings.moneyWithCurrencyFormat : settings.moneyFormat;
      const compare = parseInt(compareValue, 10);
      const strike = compare > cents ? `<s class="purchase-options__compare">${format(compare * qty, moneyFormat)}</s>` : '';
      el.innerHTML = `${strike}<span>${format(cents * qty, moneyFormat)}</span>`;
      return true;
    }

    // The new variant's prices come from the re-rendered section; the shopper's choices stay.
    onVariantChange({ data }) {
      if (data.sectionId !== this.dataset.sectionId || String(data.productId) !== this.dataset.productId) return;

      const fresh = data.parsedHTML?.getElementById(this.list?.id);
      if (!fresh) return;

      this.list.innerHTML = fresh.innerHTML;
      this.list.dataset.hasOptions = fresh.dataset.hasOptions;
      this.hidden = fresh.dataset.hasOptions !== 'true';

      this.applyState();
    }
  }

  customElements.define('purchase-options', PurchaseOptions);
}
